import { clsx } from 'clsx';
import type { FocusEvent } from 'react';
import { useMemo, useRef, useState } from 'react';

import { useWindowListener } from '../../../../hooks/useWindowListener';
import { List } from '../../../list/list';
import { Typography } from '../../../typography/typography';
import styles from './textFieldAutocomplete.module.css';
import type { TextFieldAutocompleteProps } from './textFieldAutocomplete.types';

export const TextFieldAutocomplete = ({
  errorMessage,
  handleClose,
  isOpen,
  name,
  onSelection,
  options,
  popoverFooter,
}: TextFieldAutocompleteProps) => {
  // Sanitise options to remove empty strings
  const sanitisedOptions = useMemo(
    () => options.filter((option) => option !== ''),
    [options]
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<null | number>(null);

  const handleClickAway = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      popoverRef.current?.contains(event.target as Node) ||
      !document.contains(event.target as Node) ||
      target.getAttribute('name') === name
    ) {
      return;
    } else {
      setSelectedIndex(null);
      handleClose();
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setSelectedIndex(null);
        handleClose();
        return;
      case 'ArrowDown':
        event.preventDefault();
        handleArrowDown(selectedIndex);
        return;
      case 'ArrowUp':
        event.preventDefault();
        handleArrowUp(selectedIndex);
        return;
      case 'Enter':
        event.preventDefault();
        handleEnter(selectedIndex);
        return;
      default:
        if (selectedIndex) {
          setSelectedIndex(null);
        }
    }
  };

  const handleArrowDown = (selectedIndex: number | null) => {
    if (isOpen) {
      const newIndex =
        selectedIndex === null || selectedIndex === sanitisedOptions.length - 1
          ? 0
          : selectedIndex + 1;
      setSelectedIndex(newIndex);

      document
        .getElementById(sanitisedOptions[newIndex])
        ?.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleArrowUp = (selectedIndex: number | null) => {
    if (isOpen) {
      const newIndex =
        selectedIndex === null || selectedIndex === 0
          ? sanitisedOptions.length - 1
          : selectedIndex - 1;
      setSelectedIndex(newIndex);

      document
        .getElementById(sanitisedOptions[newIndex])
        ?.scrollIntoView({ block: 'nearest' });
    }
  };

  const handleEnter = (selectedIndex: number | null) => {
    if (selectedIndex !== null) {
      onSelection(sanitisedOptions[selectedIndex]);
      setSelectedIndex(null);
    }
  };

  useWindowListener('keydown', handleKeyPress);

  useWindowListener('click', handleClickAway);

  return (
    <div
      className={clsx(styles['textFieldAutocomplete_container'])}
      ref={popoverRef}
    >
      {errorMessage && (
        <Typography mt={0} color="error">
          {errorMessage}
        </Typography>
      )}
      {isOpen && sanitisedOptions.length > 0 && (
        <div className={clsx(styles['textFieldAutocomplete_alignment'])}>
          <div className={clsx(styles['textFieldAutocomplete_options'])}>
            <div className={clsx(styles['textFieldAutocomplete_list'])}>
              <List label={`${name}-list`}>
                {sanitisedOptions.map((option, index) => (
                  <div id={option} key={option}>
                    <List.Item
                      ariaLabel={option}
                      py={4}
                      px={12}
                      selected={selectedIndex === index}
                      onClick={() => onSelection(option)}
                      onBlur={(event: FocusEvent<HTMLLIElement>) => {
                        // Check if focus is moving outside the autocomplete container
                        const relatedTarget =
                          event.relatedTarget as HTMLElement;
                        if (
                          !popoverRef.current?.contains(relatedTarget) &&
                          relatedTarget?.getAttribute('name') !== name
                        ) {
                          setSelectedIndex(null);
                          handleClose();
                        }
                      }}
                    >
                      <Typography>{option}</Typography>
                    </List.Item>
                  </div>
                ))}
              </List>
            </div>

            {popoverFooter && popoverFooter}
          </div>
        </div>
      )}
    </div>
  );
};
